import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { HashRouter, Routes, Route, Link, useSearchParams } from "react-router-dom";
import { 
  Smartphone, 
  BrainCircuit, 
  Building2, 
  Cloud, 
  Menu, 
  X, 
  Send,
  ArrowRight,
  Mail,
  Phone,
  ArrowLeft,
  Check,
  Loader2,
  CreditCard,
  Lock,
  Shield
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navbar Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);

  const navigateToSection = (sectionId) => {
    // If not on homepage, navigate to homepage with section hash
    if (window.location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsOpen(false);
  };

  const navigateToHome = () => {
    if (window.location.pathname !== '/') {
      window.location.href = '/';
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  return (
    <nav 
      data-testid="navbar" 
      className="fixed top-0 left-0 right-0 z-50 glassmorphism"
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a 
            href="/" 
            data-testid="navbar-logo"
            className="flex items-center gap-3"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-pink-600 flex items-center justify-center shadow-lg shadow-violet-500/25">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 6L3 12L8 18" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 6L21 12L16 18" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 4L10 20" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <span className="text-2xl font-bold font-['Outfit']">
              <span className="text-zinc-900">Soft</span><span className="bg-gradient-to-r from-violet-600 to-pink-600 bg-clip-text text-transparent">Development</span>
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => navigateToSection('services')}
              data-testid="nav-services"
              className="text-zinc-600 hover:text-zinc-900 transition-colors font-medium"
            >
              Services
            </button>
            <button 
              onClick={() => navigateToSection('about')}
              data-testid="nav-about"
              className="text-zinc-600 hover:text-zinc-900 transition-colors font-medium"
            >
              About
            </button>
            <button 
              onClick={() => navigateToSection('contact')}
              data-testid="nav-contact"
              className="text-zinc-600 hover:text-zinc-900 transition-colors font-medium"
            >
              Contact
            </button>
            <button 
              onClick={() => navigateToSection('contact')}
              data-testid="nav-cta"
              className="btn-primary"
            >
              Get Started
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
            className="md:hidden p-2 text-zinc-900"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div 
            data-testid="mobile-menu"
            className="md:hidden py-4 border-t border-zinc-100"
          >
            <div className="flex flex-col gap-4">
              <button 
                onClick={() => navigateToSection('services')}
                className="text-zinc-600 hover:text-zinc-900 transition-colors font-medium py-2"
              >
                Services
              </button>
              <button 
                onClick={() => navigateToSection('about')}
                className="text-zinc-600 hover:text-zinc-900 transition-colors font-medium py-2"
              >
                About
              </button>
              <button 
                onClick={() => navigateToSection('contact')}
                className="text-zinc-600 hover:text-zinc-900 transition-colors font-medium py-2"
              >
                Contact
              </button>
              <button 
                onClick={() => navigateToSection('contact')}
                className="btn-primary w-full"
              >
                Get Started
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToServices = () => {
    const element = document.getElementById('services');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      data-testid="hero-section"
      className="min-h-screen flex items-center justify-center hero-bg relative overflow-hidden pt-20"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1920&q=80')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      ></div>
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-transparent to-white/90"></div>

      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-br from-violet-200 to-pink-200 rounded-full blur-3xl opacity-60"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-gradient-to-br from-cyan-200 to-emerald-200 rounded-full blur-3xl opacity-50"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-orange-100 to-amber-100 rounded-full blur-3xl opacity-40"></div>
      </div>

      <div className="container-custom relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Caption */}
          <p 
            data-testid="hero-caption"
            className="text-sm font-medium tracking-widest uppercase bg-gradient-to-r from-violet-600 via-pink-600 to-orange-500 bg-clip-text text-transparent mb-6 opacity-0 animate-fade-in-up"
          >
            Software Development Excellence
          </p>

          {/* Headline */}
          <h1 
            data-testid="hero-headline"
            className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] text-zinc-900 mb-8 opacity-0 animate-fade-in-up animation-delay-200"
          >
            Building the Soft Layer of{' '}
            <span className="bg-gradient-to-r from-violet-600 via-pink-500 to-orange-500 bg-clip-text text-transparent">Hard Tech</span>
          </h1>

          {/* Subheadline */}
          <p 
            data-testid="hero-subheadline"
            className="text-lg md:text-xl leading-relaxed text-zinc-500 mb-12 max-w-2xl mx-auto opacity-0 animate-fade-in-up animation-delay-400"
          >
            We craft elegant, scalable software solutions for forward-thinking businesses. 
            From AI integrations to enterprise systems.
          </p>

          {/* CTA Buttons */}
          <div 
            className="flex flex-col sm:flex-row gap-4 justify-center opacity-0 animate-fade-in-up animation-delay-600"
          >
            <button 
              onClick={scrollToContact}
              data-testid="hero-cta-primary"
              className="btn-primary inline-flex items-center gap-2"
            >
              Start a Project
              <ArrowRight size={18} />
            </button>
            <button 
              onClick={scrollToServices}
              data-testid="hero-cta-secondary"
              className="btn-secondary inline-flex items-center gap-2"
            >
              View Our Services
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Smartphone,
      title: "Web & Mobile Development",
      description: "High-performance React & Native applications built with modern frameworks and best practices.",
      span: "md:col-span-2",
      gradient: "from-violet-500 to-purple-600",
      bgColor: "bg-violet-50",
      borderColor: "border-violet-200",
      iconColor: "#8B5CF6"
    },
    {
      icon: BrainCircuit,
      title: "AI/ML Integrations",
      description: "Smart algorithms and machine learning solutions to automate and enhance your business logic.",
      span: "md:col-span-1",
      gradient: "from-pink-500 to-rose-600",
      bgColor: "bg-pink-50",
      borderColor: "border-pink-200",
      iconColor: "#EC4899"
    },
    {
      icon: Building2,
      title: "Enterprise Software",
      description: "Scalable architectures designed to handle big data and complex business requirements.",
      span: "md:col-span-1",
      gradient: "from-orange-500 to-amber-600",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200",
      iconColor: "#F97316"
    },
    {
      icon: Cloud,
      title: "Cloud Infrastructure",
      description: "AWS, Azure, and GCP managed services with optimized deployment and cost-effective scaling.",
      span: "md:col-span-2",
      gradient: "from-emerald-500 to-teal-600",
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-200",
      iconColor: "#10B981"
    }
  ];

  return (
    <section 
      id="services" 
      data-testid="services-section"
      className="py-16 md:py-20 bg-gradient-to-b from-white to-zinc-50"
    >
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p 
            data-testid="services-caption"
            className="text-sm font-medium tracking-widest uppercase text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-pink-600 mb-4"
          >
            What We Do
          </p>
          <h2 
            data-testid="services-title"
            className="text-4xl md:text-5xl font-semibold tracking-tight text-zinc-900"
          >
            Our Services
          </h2>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, index) => (
            <div 
              key={index}
              data-testid={`service-card-${index}`}
              className={`group relative overflow-hidden rounded-2xl border-2 ${service.borderColor} bg-white p-8 transition-all duration-500 hover:shadow-2xl hover:shadow-zinc-200/50 hover:-translate-y-1 ${service.span}`}
            >
              {/* Gradient top border on hover */}
              <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${service.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}></div>
              
              {/* Background glow on hover */}
              <div className={`absolute -top-20 -right-20 w-40 h-40 ${service.bgColor} rounded-full blur-3xl opacity-0 group-hover:opacity-60 transition-opacity duration-500`}></div>
              
              <div className={`relative z-10 w-16 h-16 rounded-2xl ${service.bgColor} border ${service.borderColor} flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110`}>
                <service.icon className="w-8 h-8" strokeWidth={1.5} style={{color: service.iconColor}} />
              </div>
              <h3 className="relative z-10 text-2xl font-semibold text-zinc-900 mb-3">
                {service.title}
              </h3>
              <p className="relative z-10 text-zinc-500 leading-relaxed">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section 
      id="about" 
      data-testid="about-section"
      className="py-16 md:py-20 bg-gradient-to-br from-violet-50 via-pink-50 to-orange-50"
    >
      <div className="container-custom">
        <div className="max-w-4xl mx-auto text-center">
          <p 
            data-testid="about-caption"
            className="text-sm font-medium tracking-widest uppercase bg-gradient-to-r from-violet-600 to-pink-600 bg-clip-text text-transparent mb-4"
          >
            About Us
          </p>
          <h2 
            data-testid="about-title"
            className="text-4xl md:text-5xl font-semibold tracking-tight text-zinc-900 mb-6"
          >
            Engineering with Empathy
          </h2>
          <p 
            data-testid="about-description"
            className="text-lg leading-relaxed text-zinc-500 mb-6"
          >
            At SoftDevelopment.club, we believe code is just the medium. Our true product 
            is the seamless experience we create for your users.
          </p>
          <p className="text-lg leading-relaxed text-zinc-500 mb-10">
            With years of experience across diverse industries, our team combines technical 
            excellence with a deep understanding of user needs to deliver solutions that 
            truly make a difference.
          </p>
          <div className="flex justify-center gap-12">
            <div>
              <p className="text-4xl font-bold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">50+</p>
              <p className="text-zinc-500">Projects Delivered</p>
            </div>
            <div>
              <p className="text-4xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">98%</p>
              <p className="text-zinc-500">Client Satisfaction</p>
            </div>
            <div>
              <p className="text-4xl font-bold bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">24/7</p>
              <p className="text-zinc-500">Support Available</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    projectType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.status === 'success') {
        toast.success('Message sent successfully! We\'ll get back to you soon.');
        setFormData({ name: '', email: '', projectType: '', message: '' });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contact" 
      data-testid="contact-section"
      className="py-16 md:py-20 bg-white relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-violet-100 to-pink-100 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-gradient-to-br from-orange-100 to-amber-100 rounded-full blur-3xl opacity-50 translate-y-1/2 -translate-x-1/2"></div>
      
      <div className="container-custom relative z-10">
        <div className="max-w-2xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <p 
              data-testid="contact-caption"
              className="text-sm font-medium tracking-widest uppercase bg-gradient-to-r from-violet-600 to-pink-600 bg-clip-text text-transparent mb-4"
            >
              Get In Touch
            </p>
            <h2 
              data-testid="contact-title"
              className="text-4xl md:text-5xl font-semibold tracking-tight text-zinc-900 mb-4"
            >
              Start Your Project
            </h2>
            <p className="text-lg text-zinc-500">
              Have a project in mind? Let's discuss how we can help.
            </p>
          </div>

          {/* Contact Form */}
          <form 
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="space-y-6 bg-white/80 backdrop-blur-sm p-8 rounded-2xl border border-zinc-100 shadow-xl shadow-zinc-100/50"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-zinc-700 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="contact-name-input"
                  className="input-styled"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-zinc-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="contact-email-input"
                  className="input-styled"
                  placeholder="john@company.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="projectType" className="block text-sm font-medium text-zinc-700 mb-2">
                Project Type
              </label>
              <select
                id="projectType"
                name="projectType"
                value={formData.projectType}
                onChange={handleChange}
                required
                data-testid="contact-project-type-select"
                className="input-styled"
              >
                <option value="">Select a project type</option>
                <option value="web-development">Web Development</option>
                <option value="mobile-app">Mobile Application</option>
                <option value="ai-ml">AI/ML Integration</option>
                <option value="enterprise">Enterprise Software</option>
                <option value="cloud">Cloud Infrastructure</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-zinc-700 mb-2">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                data-testid="contact-message-textarea"
                className="textarea-styled"
                placeholder="Tell us about your project..."
                rows={5}
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="contact-submit-btn"
              className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                'Sending...'
              ) : (
                <>
                  Send Message
                  <Send size={18} />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

// Payment Page
const PaymentPage = () => {
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [cardData, setCardData] = useState({
    cardNumber: '',
    cardName: '',
    expiry: '',
    cvv: '',
    amount: ''
  });
  const [cryptoData, setCryptoData] = useState({
    network: 'TRC20',
    amount: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const handleCardChange = (e) => {
    const { name, value } = e.target;
    
    // Format card number with spaces
    if (name === 'cardNumber') {
      const formatted = value.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim();
      setCardData(prev => ({ ...prev, [name]: formatted.slice(0, 19) }));
      return;
    }
    
    // Format expiry
    if (name === 'expiry') {
      const formatted = value.replace(/\D/g, '').replace(/(\d{2})(\d)/, '$1/$2');
      setCardData(prev => ({ ...prev, [name]: formatted.slice(0, 5) }));
      return;
    }
    
    // CVV max 4 digits
    if (name === 'cvv') {
      setCardData(prev => ({ ...prev, [name]: value.slice(0, 4) }));
      return;
    }
    
    setCardData(prev => ({ ...prev, [name]: value }));
  };

  const handleCryptoChange = (e) => {
    const { name, value } = e.target;
    setCryptoData(prev => ({ ...prev, [name]: value }));
  };

  const handleCardSubmit = (e) => {
    e.preventDefault();
    setIsProcessing(true);
    
    // Simulate processing
    setTimeout(() => {
      toast.success('Payment processed successfully!');
      setIsProcessing(false);
      setCardData({ cardNumber: '', cardName: '', expiry: '', cvv: '', amount: '' });
    }, 2000);
  };

  const handleCryptoSubmit = (e) => {
    e.preventDefault();
    setIsProcessing(true);
    
    // Simulate processing
    setTimeout(() => {
      toast.success('Crypto payment initiated! Please complete the transfer.');
      setIsProcessing(false);
    }, 1500);
  };

  const cryptoAddresses = {
    TRC20: '1234567890',
    ERC20: '1234567890'
  };

  return (
    <>
      <Navbar />
      <main className="pt-28 pb-16 min-h-screen bg-gradient-to-b from-zinc-50 to-white">
        <div className="container-custom">
          {/* Header */}
          <div className="text-center mb-12">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 mb-6 transition-colors"
            >
              <ArrowLeft size={18} />
              Back to Home
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold text-zinc-900 mb-4 font-['Outfit']">
              Secure Payment
            </h1>
            <p className="text-lg text-zinc-500 max-w-xl mx-auto">
              Choose your preferred payment method to complete your transaction securely.
            </p>
          </div>

          {/* Payment Method Toggle */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="flex bg-zinc-100 rounded-xl p-1.5">
              <button
                onClick={() => setPaymentMethod('card')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-6 rounded-lg font-medium transition-all duration-300 ${
                  paymentMethod === 'card' 
                    ? 'bg-white text-zinc-900 shadow-md' 
                    : 'text-zinc-500 hover:text-zinc-700'
                }`}
              >
                <CreditCard size={20} />
                Credit Card
              </button>
              <button
                onClick={() => setPaymentMethod('crypto')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-6 rounded-lg font-medium transition-all duration-300 ${
                  paymentMethod === 'crypto' 
                    ? 'bg-white text-zinc-900 shadow-md' 
                    : 'text-zinc-500 hover:text-zinc-700'
                }`}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
                </svg>
                USDT
              </button>
            </div>
          </div>

          {/* Payment Forms */}
          <div className="max-w-2xl mx-auto">
            {paymentMethod === 'card' ? (
              /* Credit Card Form */
              <div className="bg-white rounded-2xl border border-zinc-200 p-8 shadow-lg">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center">
                    <CreditCard className="text-white" size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-zinc-900">Card Payment</h2>
                    <p className="text-sm text-zinc-500">Visa, Mastercard, American Express</p>
                  </div>
                </div>

                <form onSubmit={handleCardSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">
                      Amount (USD)
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                      <input
                        type="number"
                        name="amount"
                        value={cardData.amount}
                        onChange={handleCardChange}
                        required
                        className="input-styled pl-8"
                        placeholder="0.00"
                        min="1"
                        step="0.01"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">
                      Card Number
                    </label>
                    <input
                      type="text"
                      name="cardNumber"
                      value={cardData.cardNumber}
                      onChange={handleCardChange}
                      required
                      className="input-styled"
                      placeholder="1234 5678 9012 3456"
                      maxLength={19}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">
                      Cardholder Name
                    </label>
                    <input
                      type="text"
                      name="cardName"
                      value={cardData.cardName}
                      onChange={handleCardChange}
                      required
                      className="input-styled"
                      placeholder="John Doe"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-2">
                        Expiry Date
                      </label>
                      <input
                        type="text"
                        name="expiry"
                        value={cardData.expiry}
                        onChange={handleCardChange}
                        required
                        className="input-styled"
                        placeholder="MM/YY"
                        maxLength={5}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-2">
                        CVV
                      </label>
                      <input
                        type="password"
                        name="cvv"
                        value={cardData.cvv}
                        onChange={handleCardChange}
                        required
                        className="input-styled"
                        placeholder="•••"
                        maxLength={4}
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isProcessing}
                    className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Lock size={18} />
                        Pay ${cardData.amount || '0.00'}
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-6 flex items-center justify-center gap-2 text-sm text-zinc-400">
                  <Shield size={16} />
                  <span>256-bit SSL Encrypted Payment</span>
                </div>
              </div>
            ) : (
              /* Crypto Payment Form */
              <div className="bg-white rounded-2xl border border-zinc-200 p-8 shadow-lg">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                    <svg className="text-white" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-zinc-900">USDT Payment</h2>
                    <p className="text-sm text-zinc-500">Tether (TRC20 / ERC20)</p>
                  </div>
                </div>

                <form onSubmit={handleCryptoSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">
                      Amount (USDT)
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                      <input
                        type="number"
                        name="amount"
                        value={cryptoData.amount}
                        onChange={handleCryptoChange}
                        required
                        className="input-styled pl-8"
                        placeholder="0.00"
                        min="1"
                        step="0.01"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">
                      Select Network
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setCryptoData(prev => ({ ...prev, network: 'TRC20' }))}
                        className={`py-4 px-4 rounded-xl border-2 font-medium transition-all duration-300 ${
                          cryptoData.network === 'TRC20'
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-600'
                            : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'
                        }`}
                      >
                        <div className="text-lg font-bold">TRC20</div>
                        <div className="text-xs text-zinc-400 mt-1">Tron Network</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setCryptoData(prev => ({ ...prev, network: 'ERC20' }))}
                        className={`py-4 px-4 rounded-xl border-2 font-medium transition-all duration-300 ${
                          cryptoData.network === 'ERC20'
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-600'
                            : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'
                        }`}
                      >
                        <div className="text-lg font-bold">ERC20</div>
                        <div className="text-xs text-zinc-400 mt-1">Ethereum Network</div>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">
                      Send USDT ({cryptoData.network}) to this address
                    </label>
                    <div className="bg-zinc-50 rounded-xl p-4 border border-zinc-200">
                      <code className="text-sm text-zinc-700 break-all font-mono">
                        {cryptoAddresses[cryptoData.network]}
                      </code>
                    </div>
                    <p className="text-xs text-zinc-400 mt-2">
                      ⚠️ Only send USDT on <strong>{cryptoData.network}</strong> network. Sending on wrong network will result in permanent loss.
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isProcessing || !cryptoData.amount}
                    className="w-full h-12 rounded-full font-medium transition-all duration-300 bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:shadow-lg disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Check size={18} />
                        I've Sent the Payment
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-6 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                  <p className="text-sm text-emerald-700">
                    <strong>Note:</strong> USDT payments may take 1-30 minutes to confirm depending on network congestion. 
                    You will receive a confirmation email once the payment is verified.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

// Footer Component
const Footer = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer 
      data-testid="footer"
      className="bg-gradient-to-br from-zinc-900 via-zinc-900 to-violet-950 text-white py-16"
    >
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Logo & Description */}
          <div className="md:col-span-1">
            <a 
              href="/" 
              data-testid="footer-logo"
              className="flex items-center gap-3"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 6L3 12L8 18" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 6L21 12L16 18" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 4L10 20" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <span className="text-2xl font-bold font-['Outfit']">
                <span className="text-white">Soft</span><span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">Development</span>
              </span>
            </a>
            <p className="text-zinc-400 mt-4 text-sm leading-relaxed">
              Building the future of software with elegant, scalable solutions.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4 font-['Outfit']">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="text-zinc-400 hover:text-white transition-colors"
                >
                  Home
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('about')}
                  className="text-zinc-400 hover:text-white transition-colors"
                >
                  About
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('services')}
                  className="text-zinc-400 hover:text-white transition-colors"
                >
                  Services
                </button>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-lg font-semibold mb-4 font-['Outfit']">Legal</h4>
            <ul className="space-y-3">
              <li>
                <Link 
                  to="/terms-and-conditions"
                  className="text-zinc-400 hover:text-white transition-colors"
                >
                  Terms and Conditions
                </Link>
              </li>
              <li>
                <Link 
                  to="/privacy-policy"
                  className="text-zinc-400 hover:text-white transition-colors"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link 
                  to="/refund-policy"
                  className="text-zinc-400 hover:text-white transition-colors"
                >
                  Refund and Cancellation Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-4 font-['Outfit']">Contact Us</h4>
            <ul className="space-y-3 text-zinc-400">
              <li className="flex items-center gap-2">
                <Mail size={16} className="text-violet-400" />
                <a href="mailto:info@softdevelopment.club" className="hover:text-white transition-colors">
                  info@softdevelopment.club
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={16} className="text-violet-400" />
                <a href="tel:+359896834388" className="hover:text-white transition-colors">
                  +359 89 683 4388
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-zinc-800 mt-12 pt-8 text-center text-zinc-500 text-sm">
          © 2026 SoftDevelopment.club. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

// Main App
function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <HashRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/payment" element={<PaymentPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
        </Routes>
      </HashRouter>
    </div>
  );
}

// Home Page
const HomePage = () => {
  useEffect(() => {
    // Handle hash navigation on page load
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  return (
    <>
      <Navbar />
      <main>
        <HeroSection />
        <ServicesSection />
        <AboutSection />
        <ContactSection />
      </main>
      <Footer />
    </>
  );
};

// Pricing Page
const PricingPage = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPackage, setProcessingPackage] = useState(null);
  const [searchParams] = useSearchParams();
  
  const sessionId = searchParams.get('session_id');
  const status = searchParams.get('status');

  useEffect(() => {
    fetchPackages();
  }, []);

  useEffect(() => {
    if (sessionId && status === 'success') {
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.error('Payment was cancelled. Please try again.');
    }
  }, [sessionId, status]);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages`);
      setPackages(response.data.packages);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        toast.success(`Payment successful! Thank you for purchasing ${data.package_name}.`);
        return;
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    }
  };

  const handlePurchase = async (packageId) => {
    setProcessingPackage(packageId);
    
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/checkout`, {
        package_id: packageId,
        origin_url: originUrl
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Failed to create checkout:', error);
      toast.error('Failed to initiate payment. Please try again.');
      setProcessingPackage(null);
    }
  };

  const packageColors = {
    starter: {
      gradient: "from-emerald-500 to-teal-500",
      bg: "bg-emerald-50",
      border: "border-emerald-200",
      text: "text-emerald-600"
    },
    professional: {
      gradient: "from-violet-500 to-purple-500",
      bg: "bg-violet-50",
      border: "border-violet-200",
      text: "text-violet-600",
      popular: true
    },
    enterprise: {
      gradient: "from-orange-500 to-amber-500",
      bg: "bg-orange-50",
      border: "border-orange-200",
      text: "text-orange-600"
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-32 pb-20 min-h-screen bg-gradient-to-b from-zinc-50 to-white">
        <div className="container-custom">
          {/* Header */}
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-widest uppercase bg-gradient-to-r from-violet-600 to-pink-600 bg-clip-text text-transparent mb-4">
              Pricing
            </p>
            <h1 className="text-4xl md:text-5xl font-bold text-zinc-900 mb-4 font-['Outfit']">
              Choose Your Package
            </h1>
            <p className="text-lg text-zinc-500 max-w-2xl mx-auto">
              Select the perfect package for your project needs. All packages include professional development and dedicated support.
            </p>
          </div>

          {/* Packages Grid */}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {packages.map((pkg) => {
                const colors = packageColors[pkg.id] || packageColors.starter;
                const isProcessing = processingPackage === pkg.id;
                
                return (
                  <div 
                    key={pkg.id}
                    data-testid={`package-${pkg.id}`}
                    className={`relative rounded-2xl border-2 ${colors.border} bg-white p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${colors.popular ? 'ring-2 ring-violet-500 ring-offset-2' : ''}`}
                  >
                    {colors.popular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                        <span className="bg-gradient-to-r from-violet-600 to-pink-600 text-white text-sm font-medium px-4 py-1 rounded-full">
                          Most Popular
                        </span>
                      </div>
                    )}
                    
                    {/* Package Header */}
                    <div className="text-center mb-8">
                      <div className={`w-16 h-16 rounded-2xl ${colors.bg} ${colors.border} border flex items-center justify-center mx-auto mb-4`}>
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colors.gradient}`}></div>
                      </div>
                      <h3 className="text-2xl font-bold text-zinc-900 mb-2">{pkg.name}</h3>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className={`text-4xl font-bold ${colors.text}`}>${pkg.price}</span>
                        <span className="text-zinc-400">/ project</span>
                      </div>
                    </div>

                    {/* Features */}
                    <ul className="space-y-4 mb-8">
                      {pkg.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <div className={`w-5 h-5 rounded-full ${colors.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                            <Check className={`w-3 h-3 ${colors.text}`} />
                          </div>
                          <span className="text-zinc-600">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA Button */}
                    <button
                      onClick={() => handlePurchase(pkg.id)}
                      disabled={isProcessing}
                      data-testid={`buy-${pkg.id}-btn`}
                      className={`w-full h-12 rounded-full font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                        colors.popular 
                          ? 'bg-gradient-to-r from-violet-600 to-pink-600 text-white hover:shadow-lg hover:shadow-violet-500/25' 
                          : `${colors.bg} ${colors.text} hover:opacity-80 border ${colors.border}`
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          Get Started
                          <ArrowRight size={16} />
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Trust Badges */}
          <div className="mt-16 text-center">
            <p className="text-zinc-400 text-sm mb-4">Secure payment powered by</p>
            <div className="flex items-center justify-center gap-6">
              <div className="flex items-center gap-2 text-zinc-400">
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                </svg>
                <span className="font-medium">Stripe</span>
              </div>
              <div className="h-6 w-px bg-zinc-200"></div>
              <span className="text-zinc-400 text-sm">256-bit SSL Encryption</span>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

// Legal Page Layout
const LegalPageLayout = ({ title, children }) => {
  return (
    <>
      <Navbar />
      <main className="pt-32 pb-20 min-h-screen bg-gradient-to-b from-zinc-50 to-white">
        <div className="container-custom">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 mb-8 transition-colors"
          >
            <ArrowLeft size={18} />
            Back to Home
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold text-zinc-900 mb-8 font-['Outfit']">{title}</h1>
          <div className="prose prose-lg max-w-none prose-headings:font-['Outfit'] prose-headings:text-zinc-900 prose-p:text-zinc-600 prose-li:text-zinc-600">
            {children}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

// Privacy Policy Page
const PrivacyPolicy = () => {
  return (
    <LegalPageLayout title="Privacy Policy">
      <p className="text-zinc-500 mb-8">Last updated: January 2025</p>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">1. Introduction</h2>
      <p>Welcome to SoftDevelopment.club ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.</p>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">2. Information We Collect</h2>
      <p>We collect information that you voluntarily provide to us when you:</p>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <li>Fill out our contact form</li>
        <li>Request a quote or consultation</li>
        <li>Subscribe to our newsletter</li>
        <li>Communicate with us via email or other channels</li>
      </ul>
      <p className="mt-4">This information may include:</p>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <li>Name and contact information (email address, phone number)</li>
        <li>Company name and job title</li>
        <li>Project details and requirements</li>
        <li>Any other information you choose to provide</li>
      </ul>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">3. How We Use Your Information</h2>
      <p>We use the information we collect to:</p>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <li>Respond to your inquiries and provide customer support</li>
        <li>Send you project proposals and quotes</li>
        <li>Communicate with you about our services</li>
        <li>Improve our website and services</li>
        <li>Comply with legal obligations</li>
      </ul>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">4. Information Sharing</h2>
      <p>We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:</p>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <li>With your consent</li>
        <li>To comply with legal requirements</li>
        <li>To protect our rights and safety</li>
        <li>With service providers who assist in our operations (under strict confidentiality agreements)</li>
      </ul>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">5. Data Security</h2>
      <p>We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.</p>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">6. Your Rights</h2>
      <p>You have the right to:</p>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <li>Access the personal information we hold about you</li>
        <li>Request correction of inaccurate information</li>
        <li>Request deletion of your information</li>
        <li>Opt-out of marketing communications</li>
      </ul>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">7. Contact Us</h2>
      <p>If you have questions about this Privacy Policy or our data practices, please contact us at:</p>
      <p className="mt-4"><strong>Email:</strong> info@softdevelopment.club</p>
    </LegalPageLayout>
  );
};

// Terms and Conditions Page
const TermsAndConditions = () => {
  return (
    <LegalPageLayout title="Terms and Conditions">
      <p className="text-zinc-500 mb-8">Last updated: January 2025</p>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">1. Agreement to Terms</h2>
      <p>By accessing or using the services provided by SoftDevelopment.club ("Company," "we," "us," or "our"), you agree to be bound by these Terms and Conditions. If you disagree with any part of these terms, you may not access our services.</p>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">2. Services</h2>
      <p>SoftDevelopment.club provides software development services including:</p>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <li>Web and mobile application development</li>
        <li>AI/ML integrations and solutions</li>
        <li>Enterprise software development</li>
        <li>Cloud infrastructure setup and management</li>
        <li>Technical consulting and ongoing support</li>
      </ul>
      <p className="mt-4">The specific scope, deliverables, and timeline for each project will be outlined in a separate project agreement or statement of work.</p>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">3. Service Packages and Pricing</h2>
      <p>We offer various service packages as displayed on our pricing page. By purchasing a package, you agree to:</p>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <li>Pay the full amount as specified at the time of purchase</li>
        <li>Provide all necessary information and materials for project completion</li>
        <li>Respond to communications within reasonable timeframes</li>
        <li>Accept the deliverables as outlined in your chosen package</li>
      </ul>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">4. Client Responsibilities</h2>
      <p>As a client, you are responsible for:</p>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <li>Providing accurate, complete, and timely information required for the project</li>
        <li>Designating a primary contact person for project communications</li>
        <li>Reviewing deliverables and providing feedback within agreed timeframes</li>
        <li>Ensuring you have the rights to any content, images, or materials you provide</li>
        <li>Maintaining confidentiality of any credentials or access provided by us</li>
      </ul>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">5. Intellectual Property Rights</h2>
      <p><strong>Upon Full Payment:</strong> You will own all custom code, designs, and deliverables created specifically for your project, subject to the following:</p>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <li>Third-party libraries, frameworks, and tools remain subject to their respective licenses</li>
        <li>We retain the right to use general knowledge, techniques, and non-proprietary code patterns</li>
        <li>Any pre-existing intellectual property remains the property of its original owner</li>
      </ul>
      <p className="mt-4"><strong>Before Full Payment:</strong> All deliverables remain the property of SoftDevelopment.club until payment is received in full.</p>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">6. Payment Terms</h2>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <li>All payments are processed securely through Stripe</li>
        <li>Prices are displayed in USD unless otherwise specified</li>
        <li>Payment is due at the time of purchase for standard packages</li>
        <li>Custom projects may require milestone-based payments as agreed</li>
        <li>All fees are non-refundable except as specified in our Refund Policy</li>
      </ul>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">7. Project Timeline and Delivery</h2>
      <p>We strive to complete all projects within the estimated timeframes. However:</p>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <li>Timelines may be extended due to scope changes requested by the client</li>
        <li>Delays in receiving required information or feedback from the client may affect delivery</li>
        <li>We will communicate any anticipated delays promptly</li>
      </ul>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">8. Warranty and Support</h2>
      <p>Each package includes a specified support period during which we will:</p>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <li>Fix bugs and issues related to the delivered work</li>
        <li>Provide technical support via email</li>
        <li>Make minor adjustments as reasonably requested</li>
      </ul>
      <p className="mt-4">Major changes, new features, or work outside the original scope will be quoted separately.</p>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">9. Limitation of Liability</h2>
      <p>To the maximum extent permitted by law:</p>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <li>Our total liability shall not exceed the amount paid for the services</li>
        <li>We are not liable for any indirect, incidental, special, or consequential damages</li>
        <li>We are not responsible for any loss of data, profits, or business opportunities</li>
      </ul>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">10. Confidentiality</h2>
      <p>Both parties agree to maintain confidentiality of any proprietary information shared during the engagement. This obligation survives the termination of any agreement.</p>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">11. Termination</h2>
      <p>Either party may terminate a project with written notice. Upon termination:</p>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <li>Client is responsible for payment of work completed to date</li>
        <li>We will provide all completed deliverables upon receipt of payment</li>
        <li>Refunds, if applicable, will be processed according to our Refund Policy</li>
      </ul>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">12. Governing Law</h2>
      <p>These Terms and Conditions shall be governed by and construed in accordance with applicable laws, without regard to conflict of law principles.</p>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">13. Changes to Terms</h2>
      <p>We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting. Your continued use of our services constitutes acceptance of any modifications.</p>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">14. Contact Information</h2>
      <p>For questions about these Terms and Conditions, please contact us at:</p>
      <p className="mt-4"><strong>Email:</strong> info@softdevelopment.club</p>
    </LegalPageLayout>
  );
};

// Refund and Cancellation Policy Page
const RefundPolicy = () => {
  return (
    <LegalPageLayout title="Refund and Cancellation Policy">
      <p className="text-zinc-500 mb-8">Last updated: January 2025</p>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">1. Overview</h2>
      <p>At SoftDevelopment.club, we are committed to delivering high-quality software development services. This Refund and Cancellation Policy outlines the terms under which refunds may be issued and how cancellations are handled.</p>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">2. Cancellation Policy</h2>
      
      <h3 className="text-xl font-semibold mt-6 mb-3">2.1 Before Project Commencement</h3>
      <p>If you wish to cancel your order before we have started work on your project:</p>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <li><strong>Within 24 hours of purchase:</strong> Full refund (100%) will be issued</li>
        <li><strong>Within 48-72 hours of purchase:</strong> 90% refund will be issued (10% administrative fee retained)</li>
        <li><strong>After 72 hours but before work begins:</strong> 80% refund will be issued</li>
      </ul>
      
      <h3 className="text-xl font-semibold mt-6 mb-3">2.2 After Project Commencement</h3>
      <p>Once work has begun on your project:</p>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <li><strong>0-25% completion:</strong> 50% refund of the total project cost</li>
        <li><strong>25-50% completion:</strong> 25% refund of the total project cost</li>
        <li><strong>Beyond 50% completion:</strong> No refund available, but you will receive all completed work</li>
      </ul>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">3. Refund Eligibility</h2>
      <p>Refunds may be considered in the following circumstances:</p>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <li><strong>Service not delivered:</strong> If we fail to deliver the agreed services within a reasonable timeframe without valid reason</li>
        <li><strong>Significant deviation from specifications:</strong> If the delivered work significantly differs from the agreed project scope</li>
        <li><strong>Technical failure:</strong> If we are unable to complete the project due to technical limitations on our end</li>
      </ul>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">4. Non-Refundable Circumstances</h2>
      <p>Refunds will NOT be issued in the following cases:</p>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <li>Change of mind after project has commenced</li>
        <li>Failure to provide required information, content, or feedback in a timely manner</li>
        <li>Requests for features or changes outside the original project scope</li>
        <li>Dissatisfaction with industry-standard practices or design choices that were approved</li>
        <li>Third-party service issues (hosting, domains, external APIs) not under our control</li>
        <li>Projects completed and delivered as per the agreed specifications</li>
      </ul>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">5. Refund Process</h2>
      <p>To request a refund:</p>
      <ol className="list-decimal pl-6 space-y-2 mt-4">
        <li>Send an email to info@softdevelopment.club with the subject line "Refund Request"</li>
        <li>Include your order/project reference number</li>
        <li>Provide a detailed explanation of the reason for your refund request</li>
        <li>Allow up to 5 business days for initial review</li>
      </ol>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">6. Refund Timeline</h2>
      <p>Once a refund is approved:</p>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <li>Refunds will be processed within 7-10 business days</li>
        <li>Refunds will be issued to the original payment method used</li>
        <li>Bank processing times may add an additional 3-5 business days</li>
        <li>You will receive email confirmation when the refund is processed</li>
      </ul>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">7. Partial Deliverables</h2>
      <p>In cases where a refund is issued for a partially completed project:</p>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <li>You will receive all work completed up to the point of cancellation</li>
        <li>Source code and assets for completed portions will be provided</li>
        <li>Documentation for completed work will be included where applicable</li>
      </ul>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">8. Dispute Resolution</h2>
      <p>If you disagree with a refund decision:</p>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <li>You may request a review by replying to the decision email within 14 days</li>
        <li>Provide any additional information that supports your case</li>
        <li>A senior team member will review the case and provide a final decision within 10 business days</li>
      </ul>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">9. Chargebacks</h2>
      <p>We strongly encourage you to contact us directly before initiating a chargeback with your bank or credit card company. Chargebacks:</p>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <li>May result in immediate suspension of services</li>
        <li>Can delay resolution of your concerns</li>
        <li>May affect your ability to use our services in the future</li>
      </ul>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">10. Modifications to This Policy</h2>
      <p>We reserve the right to modify this Refund and Cancellation Policy at any time. Changes will be effective immediately upon posting on our website. The policy in effect at the time of your purchase will apply to that transaction.</p>
      
      <h2 className="text-2xl font-semibold mt-8 mb-4">11. Contact Us</h2>
      <p>If you have any questions about this Refund and Cancellation Policy, please contact us:</p>
      <p className="mt-4"><strong>Email:</strong> info@softdevelopment.club</p>
      <p className="mt-2">We aim to respond to all inquiries within 24-48 business hours.</p>
    </LegalPageLayout>
  );
};

export default App;
